import React, { useEffect, useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import { translations, galleryImages } from '../data/mock';
import { menuCategories, menuUi } from '../data/menuData';
import { useLanguage } from '../context/LanguageContext';
import MenuCategoryPanel from './MenuCategoryPanel';

const countItems = (cat) => cat.groups.reduce((n, g) => n + g.items.length, 0);

const MenuSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeKey, setActiveKey] = useState(menuCategories[0].key);
  const sectionRef = useRef(null);
  const panelRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const selectCategory = (key) => {
    setActiveKey(key);
    requestAnimationFrame(() => {
      const el = panelRef.current;
      if (el && el.getBoundingClientRect().top > window.innerHeight * 0.55) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  const activeCategory = menuCategories.find((c) => c.key === activeKey);
  const reveal = (d) => `transition-all duration-1000 ${d} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`;

  return (
    <section id="menu" ref={sectionRef} className="py-24 bg-[#121212] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 50% 50%, #c9a227 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`text-center mb-12 ${reveal('')}`}>
          <span className="inline-block text-[#c9a227] font-medium tracking-[0.2em] uppercase text-sm mb-4">{t.menu.subtitle}</span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">{t.menu.title}</h2>
          <div className="w-24 h-0.5 bg-[#c9a227] mx-auto mb-6"></div>
          <p className="text-white/60 max-w-xl mx-auto">{t.menu.description}</p>
        </div>

        <div className={`flex gap-4 overflow-x-auto pb-6 mb-12 scrollbar-hide ${reveal('delay-200')}`}>
          {galleryImages.map((item) => (
            <div key={item.label[language]} className="flex-shrink-0 w-64 md:w-72 group">
              <div className="relative overflow-hidden rounded-lg">
                <img src={item.img} alt={item.label[language]} className="w-full h-48 md:h-56 object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="font-serif text-lg font-bold text-white">{item.label[language]}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className={`text-center text-white/40 text-xs tracking-[0.3em] uppercase mb-5 ${reveal('delay-300')}`}>
          {menuUi.pickCategory[language]}
        </p>
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-5xl mx-auto mb-10 ${reveal('delay-300')}`} data-testid="menu-category-grid">
          {menuCategories.map((cat) => {
            const Icon = Icons[cat.icon] || Icons.Utensils;
            const active = cat.key === activeKey;
            const seasonal = cat.seasonal;
            const n = countItems(cat);
            return (
              <button
                key={cat.key}
                onClick={() => selectCategory(cat.key)}
                data-testid={`menu-category-${cat.key}`}
                aria-pressed={active}
                className={`relative text-left rounded-xl p-4 md:p-5 border transition-all duration-300 hover:-translate-y-1 ${
                  active
                    ? (seasonal ? 'bg-amber-500 border-amber-400 text-[#1a0f08] shadow-lg shadow-amber-500/20' : 'bg-[#c9a227] border-[#e8d48b] text-[#121212] shadow-lg shadow-[#c9a227]/20')
                    : (seasonal ? 'bg-[#2a1810] border-amber-500/30 text-white hover:border-amber-400' : 'bg-[#1e1e1e] border-white/10 text-white hover:border-[#c9a227]/50')
                } ${seasonal ? 'col-span-2 md:col-span-1' : ''}`}
              >
                {seasonal && (
                  <span className={`absolute top-2 right-2 text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full ${active ? 'bg-[#1a0f08] text-amber-300' : 'bg-amber-500/20 text-amber-300'}`}>
                    {menuUi.seasonal[language]}
                  </span>
                )}
                <Icon size={26} strokeWidth={1.5} className={active ? '' : (seasonal ? 'text-amber-400' : 'text-[#c9a227]')} />
                <h3 className="font-serif text-base md:text-lg font-bold mt-3 leading-tight">{cat.title[language]}</h3>
                <p className={`text-xs mt-1 ${active ? 'opacity-70' : 'text-white/40'}`}>
                  {n} {n === 1 ? menuUi.dish[language] : menuUi.dishes[language]}
                </p>
              </button>
            );
          })}
        </div>

        <div ref={panelRef} className={`max-w-4xl mx-auto scroll-mt-24 ${reveal('delay-300')}`}>
          <MenuCategoryPanel key={`${activeCategory.key}-${language}`} category={activeCategory} language={language} />
        </div>

        <div className={`text-center mt-12 ${reveal('delay-500')}`}>
          <p className="text-white/40 text-sm italic max-w-xl mx-auto">{t.menu.allergyNote}</p>
          <p className="text-[#c9a227]/60 text-xs mt-2">{menuUi.priceNote[language]}</p>
          <p className="text-white/30 text-xs mt-1 max-w-xl mx-auto">{menuUi.meatNote[language]}</p>
        </div>

        <div className={`text-center mt-10 ${reveal('delay-600')}`}>
          <a href="#contatti" className="inline-flex items-center gap-2 bg-[#c9a227] text-[#121212] px-8 py-4 rounded-full font-bold tracking-wide hover:bg-[#e8d48b] transition-all duration-300 shadow-lg hover:shadow-xl" data-testid="menu-cta-book">
            {t.menu.cta}
            <span>→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default MenuSection;
